<template>
    <button
        type="button"
        class="match-card"
        :class="[`result-${matchResult(match)}`, { static: !clickable }]"
        @click="selectMatch"
    >
        <div class="match-player">
            <img class="match-agent" :src="matchAgentImage(match)" alt="" />
            <div class="match-agent-info">
                <div class="match-agent-heading">
                    <strong>{{ matchAgentName(match) }}</strong>
                    <span class="placement-pill" :class="placementClass(match.position)">{{ positionText(match.position) }}</span>
                </div>
                <small>{{ secondaryText(match) }}</small>
            </div>
        </div>
        <div class="match-kda">
            <strong>{{ match.gameKda || "-" }}</strong>
                <small><b>{{ formatFixedDecimal(match.gameKd, 2) }}</b> K/D</small>
        </div>
        <div class="match-meta">
            <div v-if="matchTags(match).length || hasMoreTags(match)" class="match-tags">
                <span v-for="tag in matchTags(match)" :key="tag.key || tag.name" class="match-tag" :class="tagClass(tag)">
                    <i>{{ localizeTag(tag) }}</i><b v-if="tag.count > 1">x{{ tag.count }}</b>
                </span>
                <span v-if="hasMoreTags(match)" class="match-more-tag" aria-label="更多标签">...</span>
            </div>
            <div class="match-score">
                <strong><b>{{ scoreValue(match.selfScore) }}</b><span> : {{ scoreValue(match.enemyScore) }}</span></strong>
                <small>{{ match.modeCn || match.mode || "-" }}</small>
            </div>
        </div>
    </button>
</template>

<script>
const details = require("../../assets/js/valorantRecordDetails");

export default {
    name: "ValorantMatchCard",
    props: {
        match: {
            type: Object,
            required: true,
        },
        clickable: {
            type: Boolean,
            default: true,
        },
        showMap: {
            type: Boolean,
            default: false,
        },
    },
    methods: {
        formatFixedDecimal: details.formatFixedDecimal,
        hasMoreTags: details.hasMoreTags,
        localizeTag: details.localizeTag,
        matchResult: details.matchResult,
        matchTags: details.matchTags,
        positionText: details.positionText,
        tagClass: details.tagClass,
        selectMatch() {
            if (this.clickable) this.$emit("select", this.match);
        },
        matchDetails(match) {
            return match.recordDetails || {};
        },
        matchAgentImage(match) {
            const recordDetails = this.matchDetails(match);
            const englishName = recordDetails.heroName || match.heroName;
            return details.gamerskyAgentImage(englishName) || match.heroImageUrl || "";
        },
        matchAgentName(match) {
            const recordDetails = this.matchDetails(match);
            const englishName = recordDetails.heroName || match.heroName;
            return englishName ? details.localizeAgent(englishName) : match.heroNameCn || "-";
        },
        displayMatchTime(match) {
            const recordDetails = this.matchDetails(match);
            if (recordDetails.source !== "RIOT_PD") return match.gameTime || "-";

            const playedAt = Number(match.playTimeMillis);
            if (!Number.isFinite(playedAt) || playedAt <= 0) return match.gameTime || "-";

            const seconds = Math.max(0, Math.floor((Date.now() - playedAt) / 1000));
            if (seconds < 60) return "刚刚";
            if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟前`;
            if (seconds < 86400) return `${Math.floor(seconds / 3600)}小时前`;
            return `${Math.floor(seconds / 86400)}天前`;
        },
        secondaryText(match) {
            const time = this.displayMatchTime(match);
            const map = match.mapCn || match.map;
            return this.showMap && map ? `${time} · ${map}` : time;
        },
        placementClass(position) {
            const value = Number(position);
            if (value === 1) return "mvp";
            if (value === 2) return "second";
            if (value === 3) return "third";
            return "normal";
        },
        scoreValue(value) {
            return value !== null && value !== undefined && value !== "" ? value : "-";
        },
    },
};
</script>

<style lang="scss" scoped>
.match-card { position: relative; display: grid; grid-template-columns: minmax(0, 1fr) 67px minmax(0, 1fr); align-items: center; gap: 6px; width: 100%; min-height: 59px; padding: 10px 12px; overflow: hidden; box-sizing: border-box; border: 0; border-radius: 0; background: rgba(255,255,255,.1); -webkit-backdrop-filter: blur(10px); backdrop-filter: blur(10px); color: #fff; font: inherit; text-align: left; }
.match-card.static { cursor: default; }
.match-card::before { position: absolute; z-index: 0; top: 0; left: 0; width: 163px; height: 100%; border-left: 1.5px solid transparent; box-sizing: border-box; content: ""; pointer-events: none; }
.match-card.result-win::before { border-left-color: #5adbc0; background: linear-gradient(90deg,rgba(53,202,171,.4),rgba(53,202,171,0)); }
.match-card.result-loss::before { border-left-color: #ef5459; background: linear-gradient(90deg,rgba(226,50,82,.4),rgba(226,50,82,0)); }
.match-card > * { position: relative; z-index: 1; }
.match-player { display: flex; align-items: center; gap: 12px; width: 100%; min-width: 0; }
.match-agent { width: 30px; height: 30px; flex: 0 0 30px; border-radius: 6px; object-fit: cover; background: rgba(255,255,255,.08); }
.match-agent-info, .match-kda, .match-meta { min-width: 0; }
.match-agent-info { width: auto; flex: 1; }
.match-agent-heading { display: flex; align-items: center; gap: 4px; width: 100%; min-width: 0; }
.match-agent-info strong, .match-agent-info small, .match-kda strong, .match-kda small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.match-agent-info strong { min-width: 0; font-size: 12px; line-height: 17px; font-weight: 600; }
.match-agent-info small { margin-top: 4px; color: rgba(255,255,255,.6); font-size: 11px; line-height: 15px; }
.placement-pill { display: block; width: 35px; height: 16px; padding: 0 6px; flex: 0 0 35px; box-sizing: border-box; border-radius: 10px; background: #293746; color: #9baabc; font-size: 10px; line-height: 16px; font-weight: 600; text-align: center; white-space: nowrap; }
.placement-pill.mvp { background: #cbb765; color: #222; }
.placement-pill.second { background: #8e91a7; color: #222; }
.placement-pill.third { background: #a86243; color: #222; }
.match-kda { display: flex; flex-direction: column; align-items: center; gap: 4px; width: 67px; text-align: center; }
.match-kda strong { width: 100%; color: rgba(255,255,255,.8); font-size: 14px; line-height: 20px; font-weight: 400; }
.match-kda small { color: #717173; font-size: 11px; line-height: 15px; font-weight: 600; }
.match-kda small b { font-weight: 600; }
.result-win .match-kda small b, .result-win .match-score strong b { color: #5adbc0; }
.result-loss .match-kda small b, .result-loss .match-score strong b { color: #ef5459; }
.match-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; width: 100%; }
.match-tags { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 4px; width: 100%; min-height: 16px; }
.match-tag { display: flex; max-width: 100%; min-height: 16px; flex: 0 0 auto; overflow: hidden; border-radius: 8px; font-size: 9px; line-height: 14px; white-space: normal; }
.match-tag i, .match-tag b { display: block; min-height: 16px; padding: 1px 4px; box-sizing: border-box; font-style: normal; font-weight: 400; }
.match-tag i { min-width: 0; overflow-wrap: anywhere; }
.match-tag b { flex: 0 0 auto; white-space: nowrap; }
.match-tag.positive i, .match-tag.neutral i { background: rgba(229,194,107,.13); color: #e5c26b; }
.match-tag.positive b, .match-tag.neutral b { background: rgba(229,194,107,.5); color: #17171a; }
.match-tag.negative i { background: rgba(239,84,89,.15); color: #ef5459; }
.match-tag.negative b { background: rgba(239,84,89,.45); color: #17171a; }
.match-more-tag { display: block; min-width: 16px; height: 16px; padding: 0 4px; flex: 0 0 auto; box-sizing: border-box; border-radius: 8px; background: rgba(229,194,107,.13); color: #e5c26b; font-size: 9px; line-height: 14px; text-align: center; }
.match-score { display: flex; align-items: center; justify-content: flex-end; gap: 4px; width: 100%; white-space: nowrap; }
.match-score small { overflow: hidden; color: rgba(255,255,255,.6); font-size: 10px; line-height: 14px; text-overflow: ellipsis; }
.match-score strong { color: #717173; font-size: 10px; line-height: 14px; font-weight: 600; }
.match-score strong b { font-weight: 600; }

@media (max-width: 340px) {
    .match-card { grid-template-columns: minmax(0, 1fr) 57px minmax(0, 1fr); padding-left: 8px; padding-right: 8px; gap: 4px; }
    .match-player { gap: 8px; }
    .match-agent { width: 28px; height: 28px; flex-basis: 28px; }
    .match-agent-info strong { font-size: 11px; }
    .placement-pill { width: 31px; padding: 0 4px; flex-basis: 31px; font-size: 9px; }
    .match-kda { width: 57px; }
    .match-kda strong { font-size: 12px; }
    .match-tags { gap: 2px; }
    .match-tag, .match-more-tag { font-size: 8px; }
    .match-tag i, .match-tag b { padding-left: 3px; padding-right: 3px; }
    .match-score { gap: 2px; }
    .match-score small, .match-score strong { font-size: 9px; }
}
</style>
